import React, { useState, useEffect, useRef } from 'react';
import { Search, Database, Link as LinkIcon, AlertCircle, CheckCircle, Loader, Download, ArrowRight, List } from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';

/**
 * Page de mapping RNCP ↔ CFD ↔ ROME
 * Permet de visualiser les correspondances entre les différents référentiels
 */
const RncpMapping = () => {
  const [searchType, setSearchType] = useState('rncp'); // rncp, cfd, intitule
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [filterActiveOnly, setFilterActiveOnly] = useState(false);
  const [syncingCerts, setSyncingCerts] = useState({}); // { certId: { step: string, progress: number } }
  const [listedCertifications, setListedCertifications] = useState(new Set()); // Codes RNCP déjà listés
  const [syncQueue, setSyncQueue] = useState([]); // Queue de synchronisation
  const [isProcessingQueue, setIsProcessingQueue] = useState(false); // Traitement de la queue en cours
  const queueRef = useRef(syncQueue);

  // Mise à jour de la ref quand la queue change
  useEffect(() => {
    queueRef.current = syncQueue;
  }, [syncQueue]);

  // Charger les certifications déjà listées au montage
  useEffect(() => {
    fetchListedCertifications();
  }, []);

  // Traiter la queue automatiquement
  useEffect(() => {
    if (syncQueue.length > 0 && !isProcessingQueue) {
      processQueue();
    }
  }, [syncQueue, isProcessingQueue]);

  // Récupérer les certifications déjà dans le listing
  const fetchListedCertifications = async () => {
    try {
      const response = await api.get('/rncp/certifications');
      if (response.data.success && response.data.data.certifications) {
        const rncpCodes = new Set(
          response.data.data.certifications.map(cert => cert.rncpCode)
        );
        setListedCertifications(rncpCodes);
      }
    } catch (error) {
      console.error('Erreur chargement certifications listées:', error);
    }
  };

  // Traiter la queue de synchronisations
  const processQueue = async () => {
    if (queueRef.current.length === 0 || isProcessingQueue) return;

    setIsProcessingQueue(true);
    const cert = queueRef.current[0];

    try {
      await handleSyncCertification(cert);
    } catch (error) {
      console.error('Erreur traitement queue:', error);
    } finally {
      // Retirer le premier élément de la queue
      setSyncQueue(prev => prev.slice(1));
      setIsProcessingQueue(false);
    }
  };

  // Ajouter à la queue au lieu de synchroniser directement
  const addToQueue = (cert) => {
    const rncpCode = (cert.rncp || cert.code_rncp || '').replace(/^RNCP/i, '');

    // Vérifier si déjà dans le listing
    if (listedCertifications.has(rncpCode)) {
      toast.error(`La certification RNCP${rncpCode} est déjà dans le listing`);
      return;
    }

    // Vérifier si déjà dans la queue
    const alreadyInQueue = syncQueue.some(
      queueCert => (queueCert.rncp || queueCert.code_rncp || '').replace(/^RNCP/i, '') === rncpCode
    );

    if (alreadyInQueue) {
      toast.info(`RNCP${rncpCode} est déjà en attente de synchronisation`);
      return;
    }

    setSyncQueue(prev => [...prev, cert]);
    toast.success(`RNCP${rncpCode} ajouté à la file d'attente (${syncQueue.length + 1} en attente)`);
  };

  // Test de connexion à l'API
  const testConnection = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/kit-apprentissage/search/intitule?q=diplome&limit=1');

      if (response.data.success) {
        setConnectionStatus({
          connected: true,
          simulation: response.data._simulation || false,
          message: response.data._simulation
            ? 'Mode simulation actif (données de démonstration)'
            : 'Connexion API réussie'
        });
      }
    } catch (err) {
      setConnectionStatus({
        connected: false,
        message: err.response?.data?.message || err.message
      });
    } finally {
      setLoading(false);
    }
  };

  // Synchronisation d'une certification
  const handleSyncCertification = async (cert) => {
    const certId = `cert-${cert.rncp || cert.code_rncp || Date.now()}`;
    const rncpCode = (cert.rncp || cert.code_rncp || '').replace(/^RNCP/i, '');
    const intitule = cert.intitule_diplome || cert.intitule_rncp || '';

    const steps = [
      { name: 'Préparation', duration: 500 },
      { name: 'Scraping France Compétences', duration: 3000 },
      { name: 'Création des blocs', duration: 1000 },
      { name: 'Ajout au listing', duration: 800 },
      { name: 'Terminé', duration: 200 }
    ];

    try {
      for (let i = 0; i < steps.length; i++) {
        setSyncingCerts(prev => ({
          ...prev,
          [certId]: {
            step: steps[i].name,
            progress: ((i + 1) / steps.length) * 100,
            stepIndex: i
          }
        }));

        if (i === 1) {
          // Étape de scraping + sauvegarde
          await api.post('/rncp-scraping/scrape-and-save', {
            numeroRncp: rncpCode,
            intitule: intitule
          });
        } else {
          await new Promise(resolve => setTimeout(resolve, steps[i].duration));
        }
      }

      toast.success(`Certification RNCP${rncpCode} ajoutée au listing !`);

      // Ajouter aux certifications listées
      setListedCertifications(prev => new Set([...prev, rncpCode]));

      // Retirer après 2 secondes
      setTimeout(() => {
        setSyncingCerts(prev => {
          const newState = { ...prev };
          delete newState[certId];
          return newState;
        });
      }, 2000);

    } catch (error) {
      console.error('Erreur synchronisation:', error);

      // Gestion spécifique pour les doublons
      if (error.response?.status === 409 || error.response?.data?.code === 'DUPLICATE_CERTIFICATION') {
        toast.error(`La certification RNCP${rncpCode} est déjà dans le listing`, {
          duration: 4000
        });
      } else {
        toast.error(error.response?.data?.message || 'Erreur lors de la synchronisation');
      }

      setSyncingCerts(prev => {
        const newState = { ...prev };
        delete newState[certId];
        return newState;
      });
    }
  };

  // Recherche
  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setError('Veuillez saisir un terme de recherche');
      return;
    }

    setLoading(true);
    setError(null);
    setResults(null);

    try {
      let response;

      switch (searchType) {
        case 'rncp':
          response = await api.get(`/kit-apprentissage/enriched/${searchQuery}`);
          break;

        case 'cfd':
          response = await api.get(`/kit-apprentissage/search/cfd/${searchQuery}`);
          break;

        case 'intitule':
          response = await api.get(`/kit-apprentissage/search/intitule?q=${encodeURIComponent(searchQuery)}&limit=20`);
          break;

        default:
          throw new Error('Type de recherche inconnu');
      }

      setResults(response.data);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  // Affichage d'une certification
  const renderCertification = (cert, index) => {
    const rncpCode = (cert.rncp || cert.code_rncp || '').replace(/^RNCP/i, '');
    const certId = `cert-${cert.rncp || cert.code_rncp}`;
    const isAlreadyListed = listedCertifications.has(rncpCode);
    const isInQueue = syncQueue.some(
      queueCert => (queueCert.rncp || queueCert.code_rncp || '').replace(/^RNCP/i, '') === rncpCode
    );
    const isSyncing = !!syncingCerts[certId];

    return (
      <div key={index} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h3 className="font-semibold text-lg text-gray-900">{cert.intitule_diplome || cert.intitule_rncp}</h3>

            <div className="mt-2 space-y-1">
              {cert.rncp && (
                <div className="flex items-center text-sm">
                  <span className="font-medium text-gray-600 w-24">RNCP:</span>
                  <span className="text-blue-600 font-mono">{cert.rncp.replace('RNCP', '')}</span>
                </div>
              )}

              {cert.cfd && (
                <div className="flex items-center text-sm">
                  <span className="font-medium text-gray-600 w-24">CFD:</span>
                  <span className="text-green-600 font-mono">{cert.cfd}</span>
                </div>
              )}

              {cert.niv_fiche_RNCP && (
                <div className="flex items-center text-sm">
                  <span className="font-medium text-gray-600 w-24">Niveau:</span>
                  <span className="text-gray-700">{cert.niv_fiche_RNCP}</span>
                </div>
              )}

              {cert.nsf_rncp && cert.nsf_rncp.length > 0 && (
                <div className="flex items-center text-sm">
                  <span className="font-medium text-gray-600 w-24">NSF:</span>
                  <span className="text-purple-600">{cert.nsf_rncp.join(', ')}</span>
                </div>
              )}

              {cert.codes_nsf && cert.codes_nsf.length > 0 && (
                <div className="flex items-center text-sm">
                  <span className="font-medium text-gray-600 w-24">NSF:</span>
                  <span className="text-purple-600">{cert.codes_nsf.join(', ')}</span>
                </div>
              )}

              {cert.romeCodes && cert.romeCodes.length > 0 && (
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <div className="font-medium text-gray-700 mb-2 flex items-center gap-2">
                    <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded text-xs font-semibold">
                      ROME ({cert.romeCodesCount})
                    </span>
                    Codes métiers ROME correspondants
                  </div>
                  <div className="space-y-1">
                    {cert.romeCodes.map((rome, idx) => (
                      <div key={idx} className="flex items-center text-sm bg-blue-50 rounded p-2">
                        <span className="font-mono font-semibold text-blue-700 w-16">{rome.code}</span>
                        <span className="text-gray-700">{rome.libelle}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {cert.etat_fiche && (
                <div className="flex items-center text-sm">
                  <span className="font-medium text-gray-600 w-24">État:</span>
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                    cert.etat_fiche === 'ACTIVE' || cert.etat_fiche === 'ACTIF'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {cert.etat_fiche}
                  </span>
                </div>
              )}

              {cert.acces_apprentissage && (
                <div className="flex items-center text-sm">
                  <span className="font-medium text-gray-600 w-24">Apprentissage:</span>
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                    cert.acces_apprentissage === 'Oui' || cert.acces_apprentissage === 'OUI'
                      ? 'bg-blue-100 text-blue-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {cert.acces_apprentissage}
                  </span>
                </div>
              )}
            </div>

            {/* Bouton de synchronisation */}
            <div className="mt-4 pt-4 border-t border-gray-200">
              <button
                onClick={() => addToQueue(cert)}
                disabled={isAlreadyListed || isSyncing}
                className={`w-full px-4 py-2 rounded-lg transition-colors flex items-center justify-center gap-2 ${
                  isAlreadyListed
                    ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                    : isSyncing
                    ? 'bg-emerald-600 text-white opacity-50 cursor-not-allowed'
                    : 'bg-emerald-600 text-white hover:bg-emerald-700'
                }`}
              >
                {isAlreadyListed ? (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    Certification déjà listée
                  </>
                ) : isInQueue ? (
                  <>
                    <List className="w-4 h-4" />
                    En attente dans la queue
                  </>
                ) : isSyncing ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    Synchronisation en cours...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4" />
                    Synchroniser dans /listing-formation
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Barre de progression animée */}
        {isSyncing && (
          <div className="mt-3 overflow-hidden transition-all duration-300 ease-in-out" style={{ maxHeight: isSyncing ? '200px' : '0' }}>
            <div className="bg-gradient-to-r from-emerald-50 to-blue-50 rounded-lg p-4 border border-emerald-200">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-semibold text-emerald-900">
                  {syncingCerts[certId].step}
                </span>
                <span className="text-xs font-mono text-emerald-700 bg-white px-2 py-1 rounded">
                  {Math.round(syncingCerts[certId].progress)}%
                </span>
              </div>

              {/* Points d'étape */}
              <div className="flex items-center justify-between mb-3">
                {['Prep', 'Scrap', 'Blocs', 'Save', 'OK'].map((step, idx) => {
                  const isActive = idx <= syncingCerts[certId].stepIndex;
                  const isCurrent = idx === syncingCerts[certId].stepIndex;
                  const isCompleted = idx < syncingCerts[certId].stepIndex;

                  return (
                    <div key={idx} className="flex flex-col items-center gap-1">
                      <div className={`relative w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 ${
                        isCompleted ? 'bg-green-500 scale-110' :
                        isCurrent ? 'bg-emerald-500 scale-125 animate-pulse' :
                        'bg-gray-300'
                      }`}>
                        {isCompleted && (
                          <CheckCircle className="w-5 h-5 text-white animate-bounce" />
                        )}
                        {isCurrent && (
                          <Loader className="w-4 h-4 text-white animate-spin" />
                        )}
                      </div>
                      <span className={`text-[10px] font-medium transition-colors ${
                        isActive ? 'text-emerald-700' : 'text-gray-500'
                      }`}>
                        {step}
                      </span>
                    </div>
                  );
                })}
              </div>

              {/* Barre de progression */}
              <div className="relative h-2 bg-emerald-200 rounded-full overflow-hidden">
                <div
                  style={{ width: `${syncingCerts[certId].progress}%` }}
                  className="absolute h-full bg-gradient-to-r from-emerald-500 to-blue-500 rounded-full transition-all duration-500 ease-out"
                />
              </div>
            </div>
          </div>
        )}

      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
                <Database className="w-8 h-8 text-blue-600" />
                Mapping RNCP ↔ CFD ↔ ROME
              </h1>
              <p className="text-gray-600 mt-2">
                Correspondances entre référentiels: RNCP (France Compétences), CFD (Éducation Nationale), et NSF
              </p>
            </div>

            {/* Compteur de queue */}
            {syncQueue.length > 0 && (
              <div className="bg-gradient-to-r from-blue-50 to-emerald-50 border-2 border-blue-300 rounded-lg px-4 py-3 shadow-lg">
                <div className="flex items-center gap-3">
                  <List className="w-5 h-5 text-blue-600" />
                  <div>
                    <div className="text-xs font-medium text-gray-600">File d'attente</div>
                    <div className="text-lg font-bold text-blue-900">
                      {syncQueue.length} certification{syncQueue.length > 1 ? 's' : ''} en attente
                    </div>
                  </div>
                  {isProcessingQueue && (
                    <Loader className="w-5 h-5 text-emerald-600 animate-spin" />
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Test de connexion */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">État de la connexion API</h2>
              <p className="text-sm text-gray-600">Kit Apprentissage - Certif Info</p>
            </div>
            <button
              onClick={testConnection}
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {loading ? (
                <>
                  <Loader className="w-4 h-4 animate-spin" />
                  Test en cours...
                </>
              ) : (
                <>
                  <LinkIcon className="w-4 h-4" />
                  Tester la connexion
                </>
              )}
            </button>
          </div>

          {connectionStatus && (
            <div className={`mt-4 p-4 rounded-lg flex items-start gap-3 ${
              connectionStatus.connected
                ? 'bg-green-50 border border-green-200'
                : 'bg-red-50 border border-red-200'
            }`}>
              {connectionStatus.connected ? (
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              ) : (
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              )}
              <div>
                <div className={`font-medium ${
                  connectionStatus.connected ? 'text-green-900' : 'text-red-900'
                }`}>
                  {connectionStatus.connected ? 'Connexion établie' : 'Connexion échouée'}
                </div>
                <div className={`text-sm mt-1 ${
                  connectionStatus.connected ? 'text-green-700' : 'text-red-700'
                }`}>
                  {connectionStatus.message}
                </div>
                {connectionStatus.simulation && (
                  <div className="text-sm mt-2 text-orange-700 bg-orange-50 border border-orange-200 rounded p-2">
                    ⚠️ Mode simulation activé - Configurez KIT_APPRENTISSAGE_TOKEN dans .env pour utiliser l'API réelle
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Recherche */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Rechercher une certification</h2>

          {/* Type de recherche */}
          <div className="flex gap-2 mb-4">
            <button
              onClick={() => setSearchType('rncp')}
              className={`px-4 py-2 rounded-lg font-medium transition ${
                searchType === 'rncp'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Par RNCP
            </button>
            <button
              onClick={() => setSearchType('cfd')}
              className={`px-4 py-2 rounded-lg font-medium transition ${
                searchType === 'cfd'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Par CFD
            </button>
            <button
              onClick={() => setSearchType('intitule')}
              className={`px-4 py-2 rounded-lg font-medium transition ${
                searchType === 'intitule'
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Par intitulé
            </button>
          </div>

          {/* Champ de recherche */}
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                placeholder={
                  searchType === 'rncp' ? 'Ex: 35044 ou RNCP35044' :
                  searchType === 'cfd' ? 'Ex: 32033304' :
                  'Ex: comptabilité, gestion, sport...'
                }
                className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
            </div>
            <button
              onClick={handleSearch}
              disabled={loading}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {loading ? (
                <>
                  <Loader className="w-4 h-4 animate-spin" />
                  Recherche...
                </>
              ) : (
                'Rechercher'
              )}
            </button>
          </div>
        </div>

        {/* Erreur */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-medium text-red-900">Erreur de recherche</div>
              <div className="text-sm text-red-700 mt-1">{error}</div>
            </div>
          </div>
        )}

        {/* Résultats */}
        {results && (
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">
                Résultats de la recherche
              </h2>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={filterActiveOnly}
                    onChange={(e) => setFilterActiveOnly(e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 text-green-600 focus:ring-green-500 cursor-pointer"
                  />
                  <span className="text-sm text-gray-700 group-hover:text-green-700 transition-colors flex items-center gap-1">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    Actifs uniquement
                  </span>
                </label>
                <div className="text-sm text-gray-600">
                  {results.total || results.count || 0} résultat(s)
                </div>
              </div>
            </div>


            <div className="space-y-4">
              {searchType === 'intitule' && results.data && results.data.length > 0 ? (
                results.data
                  .filter(cert => !filterActiveOnly || cert.etat_fiche === 'ACTIF' || cert.etat_fiche === 'ACTIVE')
                  .map((cert, index) => renderCertification(cert, index))
              ) : (searchType === 'rncp' || searchType === 'cfd') && results.data ? (
                (!filterActiveOnly || results.data.etat_fiche === 'ACTIF' || results.data.etat_fiche === 'ACTIVE') ? (
                  renderCertification(results.data, 0)
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    Certification inactive (filtrée)
                  </div>
                )
              ) : results.data && results.data.length > 0 ? (
                results.data
                  .filter(cert => !filterActiveOnly || cert.etat_fiche === 'ACTIF' || cert.etat_fiche === 'ACTIVE')
                  .map((cert, index) => renderCertification(cert, index))
              ) : (
                <div className="text-center py-8 text-gray-500">
                  Aucun résultat trouvé
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RncpMapping;
